import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GISServiceTypes } from '@rsos/reduxDataStore';
import {
  selectHasPureWirelessCapability,
  selectPureWirelessVisible,
} from '@rsos/reduxDataStore/src/features/additionalLayers/additionalLayersSlice';
import type { RootState } from '../../app/rootReducer';
import {
  trackLayerInitState,
  trackLayerVisibleDuration,
  type LayerInitSource,
  type LayerVisibleEndReason,
  type LayerVisibleType,
} from '../JurisdictionView/Map/gainsight/gisTrackings';

const PURE_WIRELESS_LAYER_NAME = 'Pure Wireless';

interface LayerSnapshot {
  id: string;
  name: string;
  layerType: LayerVisibleType;
  source: LayerInitSource;
  visible: boolean;
}

interface OpenPeriod {
  name: string;
  layerType: LayerVisibleType;
  startedAt: number;
  wasDefault: boolean;
}

type OpenPeriods = Map<string, OpenPeriod>;

interface SpatialLayer {
  key: string;
  name?: string;
  title?: string;
  isChecked?: boolean;
}

/** First-seen visibility, persisted across map remounts in a page session. */
const sessionSeenLayers = new Map<string, boolean>();

export const resetMapLayerAnalyticsSession = (): void => {
  sessionSeenLayers.clear();
};

const closePeriod = (
  openPeriods: OpenPeriods,
  id: string,
  endReason: LayerVisibleEndReason,
) => {
  const period = openPeriods.get(id);
  if (!period) return;

  openPeriods.delete(id);
  trackLayerVisibleDuration({
    layerName: period.name,
    durationMs: Date.now() - period.startedAt,
    wasDefault: period.wasDefault,
    layerType: period.layerType,
    endReason,
  });
};

/** Tracks layer initialization and visible periods from Redux state. */
const useMapLayerAnalytics = (): void => {
  const staticLayers = useSelector(
    (state: RootState) => state.spatialLayers?.staticLayers,
  );
  const dynamicLayers = useSelector(
    (state: RootState) => state.spatialLayers?.dynamicLayers,
  );
  const gisLayers = useSelector(
    (state: RootState) => state.gisServices?.layers,
  );
  const gisOnDemandLayers = useSelector(
    (state: RootState) => state.gisServices?.onDemandLayers,
  );
  const isPureWirelessVisible = useSelector(selectPureWirelessVisible);
  const hasPureWireless = useSelector(selectHasPureWirelessCapability);
  const mapProvider = useSelector(
    (state: RootState) => state.sinatra?.ui?.mapProvider ?? '',
  );

  const catalog = useMemo<LayerSnapshot[]>(() => {
    const spatial = [
      ...((staticLayers as SpatialLayer[] | undefined) ?? []),
      ...((dynamicLayers as SpatialLayer[] | undefined) ?? []),
    ].map(layer => ({
      id: `spatial:${layer.key}`,
      name: layer.name || layer.title || layer.key,
      layerType: 'spatial' as const,
      source: 'configured' as const,
      visible: Boolean(layer.isChecked),
    }));

    const gis = [...(gisLayers ?? []), ...(gisOnDemandLayers ?? [])]
      // Basemap `active` is always true; dedicated events track its usage.
      .filter(layer => layer.service?.service_type !== GISServiceTypes.basemap)
      .map(layer => ({
        id: `gis:${layer.id}`,
        name: layer.title,
        layerType: 'gis' as const,
        source: 'configured' as const,
        visible: Boolean(layer.active),
      }));

    // Event presence is the exposure denominator; omit unavailable layers.
    const additional = hasPureWireless
      ? [
          {
            id: 'additional:pure_wireless',
            name: PURE_WIRELESS_LAYER_NAME,
            layerType: 'additional' as const,
            // Visibility is restored from local persistence.
            source: 'persisted_user' as const,
            visible: isPureWirelessVisible,
          },
        ]
      : [];

    return [...spatial, ...gis, ...additional];
  }, [
    staticLayers,
    dynamicLayers,
    gisLayers,
    gisOnDemandLayers,
    isPureWirelessVisible,
    hasPureWireless,
  ]);

  const openPeriods = useRef<OpenPeriods>(new Map());

  useEffect(() => {
    const now = Date.now();
    const presentIDs = new Set<string>();

    catalog.forEach(layer => {
      presentIDs.add(layer.id);

      // Layer sources arrive independently, so snapshot each on first sighting.
      if (!sessionSeenLayers.has(layer.id)) {
        sessionSeenLayers.set(layer.id, layer.visible);
        trackLayerInitState({
          layerName: layer.name,
          enabled: layer.visible,
          source: layer.source,
          layerType: layer.layerType,
          mapProvider,
        });
      }

      const isOpen = openPeriods.current.has(layer.id);

      if (layer.visible && !isOpen) {
        openPeriods.current.set(layer.id, {
          name: layer.name,
          layerType: layer.layerType,
          startedAt: now,
          wasDefault: sessionSeenLayers.get(layer.id) ?? false,
        });
        return;
      }

      if (!layer.visible && isOpen) {
        closePeriod(openPeriods.current, layer.id, 'layer_off');
      }
    });

    // Removed visible layers become right-censored periods.
    [...openPeriods.current.keys()]
      .filter(id => !presentIDs.has(id))
      .forEach(id => closePeriod(openPeriods.current, id, 'map_unmount'));
  }, [catalog, mapProvider]);

  useEffect(() => {
    const periods = openPeriods.current;
    const flushAll = (endReason: LayerVisibleEndReason) =>
      [...periods.keys()].forEach(id => closePeriod(periods, id, endReason));

    // pagehide covers regular unload and the back/forward cache.
    const handlePageHide = () => flushAll('session_end');
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      flushAll('map_unmount');
    };
  }, []);
};

export default useMapLayerAnalytics;
